/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import getMultipleSkus from '@/api/getMultipleSkus';
import SkuCardDesktop from '@/components/common/cards/sku/desktop/SkuCardDesktop';
import SkuCardMobile from '@/components/common/cards/sku/mobile/SkuCardMobile';
import ResponsiveComponent from '@/components/common/ResponsiveComponent';
import { SkuCardData } from '@/types/cards';
import { CustomHitsProps } from '@/types/props/components';
import { SkuDTO } from '@/types/api/sku';
import { getSkuMainImage } from '@/utilites/get-sku-main-image';
import { getTrendingPercent } from '@/utilites/sku-operations';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useHits } from 'react-instantsearch';
import translationFallback from '@/utilites/translations/translationFallback';
import useWishlist from '@/hooks/useWishlist';

export default function CustomHits({
  wishlistContext,
  user,
  tCommon,
  push,
  windowWidth,
  escapeHTML,
  transformItems,
  ...props
}: CustomHitsProps) {
  const { isInWishlist } = useWishlist();
  const { items } = useHits<SkuDTO>(props);
  const [skus, setSkus] = useState<SkuDTO[] | null>(Array(21).fill(null));

  const {
    data: backendSkusData = null,
    isLoading,
    isPending,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['sku/multiple', items.map(item => item.slug), user],
    queryFn: ({ queryKey }) =>
      getMultipleSkus(queryKey[1] as string[], Number((queryKey[1] as string[]).length)),
  });

  useEffect(() => {
    const algoliaItems = items.map(item => {
      const parsedName = JSON.parse(JSON.stringify({ name: item.name })).name;
      const cleanName = parsedName.replace(/\\"/g, '"').replace(/\\{2}/g, '');

      return {
        ...item,
        name: cleanName,
        isInWishlist: isInWishlist(item.slug) ?? false,
        trendingPercent: 0,
        mainImageUrl: getSkuMainImage(item.mainImageUrl),
      };
    });

    if (isLoading || isFetching) {
      setSkus(Array(21).fill(null));
      return;
    }

    if (!isLoading && backendSkusData) {
      const backendSkusFormatted = backendSkusData.map(sku => ({
        ...sku,
        __position: algoliaItems.find(item => item.slug === sku.slug)?.__position || 999999,
        isInWishlist: isInWishlist(sku.slug) ?? false,
        trendingPercent: sku?.orders
          ? getTrendingPercent({
              orders: sku.orders,
              retailPrice: sku.retailPrice,
            })
          : 0,
        mainImageUrl: getSkuMainImage(sku.mainImageUrl),
      }));

      const skusInBackend = backendSkusData.map(sku => sku.slug);
      const skusNotInBackend = algoliaItems.filter(item => !skusInBackend.includes(item.slug));

      const skusMix = [...backendSkusFormatted, ...skusNotInBackend];

      setSkus(skusMix.sort((a, b) => (a.__position || 0) - (b.__position || 0)) as SkuDTO[]);
      return;
    }

    if (isError && algoliaItems.length > 0) {
      setSkus(algoliaItems);
      return;
    }

    if (algoliaItems.length === 0) {
      setSkus(null);
    }
  }, [isLoading, isFetching, isError, backendSkusData, items, isPending, isInWishlist]);

  return (
    <ul className="!tw-m-0 tw-grid tw-w-full tw-grid-cols-3 tw-gap-4 !tw-px-0 tw-pb-8 max-md:tw-grid-cols-2 max-sm:tw-grid-cols-1">
      {skus === null ? (
        <span>
          {translationFallback(
            'no_products_found',
            tCommon('no_products_found'),
            'No products found'
          )}
        </span>
      ) : (
        <ResponsiveComponent
          windowWidth={windowWidth}
          MobileComponent={skus.map((sku, index) => (
            <li key={sku?.id || index} className="tw-list-none">
              <SkuCardMobile sku={sku} />
            </li>
          ))}
          DesktopComponent={skus.map((sku, index) => (
            <li key={sku?.id || index} className="tw-list-none">
              <SkuCardDesktop sku={sku} />
            </li>
          ))}
        />
      )}
    </ul>
  );
}
